/* eslint-disable prefer-arrow-callback */
import './card.css';
import React from 'react';
import CustomErrorBoundary from '~go/web/src/js/components/common/error_states/custom_error_boundary';
import { clsx } from '../../../utils/clsx/clsx';
import Text from '../text/text';
import { COLOR_NAMES } from '../../../constants/colors';
import Icon from '../icon/icon';

type ColorName = typeof COLOR_NAMES[number];
type BorderRadius = false | 'sm' | 'md' | 'lg';

type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  border?: boolean;
  borderColor?: ColorName;
  bgColor?: 'transparent' | ColorName;
  borderRadius?: BorderRadius;
  className?: string;
  style?: React.CSSProperties;
  whiteOverlay?: boolean;
  children: React.ReactNode;
};

const Card = React.forwardRef<HTMLDivElement, CardProps>(function CustomCard(
  {
    border = true,
    borderColor = 'light-gray',
    bgColor = 'white',
    borderRadius = 'md',
    className = '',
    style = {},
    whiteOverlay = false,
    children,
    ...props
  },
  ref
) {
  return (
    <div
      ref={ref}
      className={clsx(
        'custom-card d-flex flex-column w-100',
        border && 'border',
        `border-${borderColor}`,
        `bg-${bgColor}`,
        borderRadius === 'md' ? 'rounded' : `rounded-${borderRadius}`,
        whiteOverlay && 'card-white-overlay',
        className
      )}
      style={style}
      {...props}
    >
      {/* White Overlay */}
      {whiteOverlay && (
        <div
          className="position-absolute"
          style={{
            inset: 0,
            zIndex: 100,
            backgroundColor: 'rgba(255 255 255 / 0.5)',
            backdropFilter: 'blur(8px)',
          }}
        />
      )}

      <CustomErrorBoundary>{children}</CustomErrorBoundary>
    </div>
  );
});

type CardHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
  className?: string;
};

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(function CardHeader(
  { children, className = '', ...props },
  ref
) {
  return (
    <div ref={ref} className={clsx('w-100', className)} {...props}>
      {children}
    </div>
  );
});

type CardTitleProps = React.ComponentProps<typeof Text> & {
  icon?: string;
  iconVariant?: React.ComponentProps<typeof Icon>['variant'];
  iconSize?: string | number;
  variant?: React.ComponentProps<typeof Text>['variant'];
};

const CardTitle = React.forwardRef<HTMLDivElement, CardTitleProps>(function CardTitle(
  { children, className = '', icon, iconVariant = 'regular', iconSize = 'inherit', variant = 'h4', ...props },
  ref
) {
  return (
    <Text
      ref={ref}
      variant={variant}
      component="div"
      className={clsx('d-flex align-items-center gap-1', className)}
      {...props}
    >
      {icon && <Icon variant={iconVariant} icon={icon} size={iconSize} />}
      {children}
    </Text>
  );
});

type CardBodyProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

const CardBody = React.forwardRef<HTMLDivElement, CardBodyProps>(function CardBody(
  { children, className = '', style = {}, ...props },
  ref
) {
  return (
    <div ref={ref} style={{ flex: '1 1 auto', ...style }} className={clsx('w-100', className)} {...props}>
      {children}
    </div>
  );
});

type CardFooterProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
  className?: string;
};

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(function CardFooter(
  { children, className = '', ...props },
  ref
) {
  return (
    <div ref={ref} className={clsx('w-100', className)} {...props}>
      {children}
    </div>
  );
});

export default Object.assign(Card, {
  Header: CardHeader,
  Title: CardTitle,
  Body: CardBody,
  Footer: CardFooter,
});
